import {
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
} from "../modules/PushSubscription";
import { getPreferredLanguage } from "./languageUtils";

/**
 * Handles push notification subscription.
 * @param accountToken - The account's token for authentication.
 * @param vapidKey - VAPID public key for Web Push.
 */
export const handlePushSubscription = async (
    accountToken: string,
    vapidKey: string
) => {
    if (!("serviceWorker" in navigator)) {
        console.error("Service Worker is not supported in this browser.");
        return;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        console.log("Service Worker ready:", registration);

        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidKey,
        });

        console.log("Push subscription object generated:", subscription);

        const userLanguage = getPreferredLanguage();

        // Send subscription to the server
        await subscribeToPushNotifications({
            account_id: accountToken,
            subscription,
            language: userLanguage,
        });

        console.log("Push subscription registered successfully.");
    } catch (err) {
        console.error("Error creating subscription:", err);
    }
};

/**
 * Requests notification permissions from the user and creates a subscription if granted.
 * @param accountToken - The account's token for authentication.
 * @param vapidKey - VAPID public key for Web Push.
 */
export const requestNotificationPermission = async (
    accountToken: string,
    vapidKey: string
) => {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
        console.log(
            "Notifications permission granted. Creating subscription..."
        );
        await handlePushSubscription(accountToken, vapidKey);
    } else if (permission === "denied") {
        console.warn("Notifications permission denied.");
    } else {
        console.log("Notifications permission request dismissed.");
    }
};

/**
 * Handles push notification unsubscription.
 * @param accountToken - The user's token for authentication.
 */
export const handlePushUnsubscription = async (accountToken: string) => {
    if (!("serviceWorker" in navigator)) {
        console.error("Service Worker is not supported in this browser.");
        return;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            console.log("Unsubscribing from push notifications...");
            await subscription.unsubscribe();

            console.log("Notifying server to remove subscription...");
            await unsubscribeFromPushNotifications(accountToken);

            console.log("Push subscription successfully removed.");
        } else {
            console.log("No active push subscription found.");
        }
    } catch (error) {
        console.error("Error during unsubscription:", error);
    }
};
